import { useState } from "react";
import { z } from "zod";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useGetCrmMasterDetail, type CrmRecordDetail, type CrmRecordType } from "@/lib/api/crm/masters";
import { useGetCrmRules, type CrmRules } from "@/lib/api/crm/rules";
import { useListCustomers } from "@/lib/api/customer";
import { useManageException } from "@/lib/api/destination";
import type { Customer } from "@/lib/api/reports";
import { CrmCustomerCreateDialog } from "@/section/crm/components/CrmCustomerCreateDialog";
import { CrmCustomerEditDialog } from "@/section/crm/components/CrmCustomerEditDialog";
import type { FormSubmitError } from "@/section/crm/form/recordFormErrors";
import type { ContactFormRow, RecordFormValues } from "@/section/crm/form/recordFormSchema";
import { emptyRecordFormValues } from "@/section/crm/form/recordFormValues";
import { catalogTagId } from "@/section/crm/form/tagOptions";

/**
 * The fields read from a clicked exception table row: the exception, the row's name (producer_or_carrier)
 * and its customer id (no_phone_number, the customer the exception points at).
 */
export const CustomerExceptionRowSchema = z.object({
  exception_id: z.string().uuid(),
  long_name: z.string().nullish(),
  id: z.string().uuid().nullish(),
});

/** A "Missing Producer or Carrier" or "Missing Destination Phone Number" exception to resolve. */
export type CustomerException =
  | { exception_type: "producer_or_carrier"; exception_id: string; name: string }
  | { exception_type: "no_phone_number"; exception_id: string; customer_id: string };

/** The customer to resolve the exception with, and why ("saved" resolves right away). */
type Resolving = { customerId: string; reason: "saved" | "chosen" | "has_destination_phone" };

function destinationTagId(rules: CrmRules): string | null {
  return catalogTagId(rules, "contact", "destination");
}

/** Whether GET /crm/rules allows a Destination phone on the record type. */
function allowsDestination(rules: CrmRules, recordType: CrmRecordType): boolean {
  const tagId = destinationTagId(rules);
  const allowedTypes = tagId ? rules.tags[tagId]?.record_types : undefined;
  return allowedTypes !== undefined && (allowedTypes === null || allowedTypes.includes(recordType));
}

/** Whether the contacts have a filled-in phone tagged Destination. */
function hasDestinationPhone(rules: CrmRules, contacts: readonly Pick<ContactFormRow, "kind" | "value" | "tag_ids">[]): boolean {
  const tagId = destinationTagId(rules);
  return tagId !== null
    && contacts.some((contact) => contact.kind === "phone" && contact.value.trim() !== "" && contact.tag_ids.includes(tagId));
}

/** An empty phone row tagged Destination, for the user to fill in. */
function destinationPhoneRow(rules: CrmRules): ContactFormRow[] {
  const tagId = destinationTagId(rules);
  return tagId ? [{ id: null, kind: "phone", value: "", label: "", tag_ids: [tagId] }] : [];
}

/** Both exceptions are about the destination phone: the record must end up with one. */
function requireDestinationPhone(rules: CrmRules, values: RecordFormValues): FormSubmitError | null {
  if (values.record_type && !allowsDestination(rules, values.record_type)) {
    return { banner: { message: "This record type can't have a Destination phone number." }, fieldErrors: [] };
  }
  return hasDestinationPhone(rules, values.contacts)
    ? null
    : { banner: { message: "Add a phone number tagged Destination." }, fieldErrors: [] };
}

/** The linked record already has a Destination phone (e.g. saved before an nx call that failed). */
function storedRecordHasDestinationPhone(rules: CrmRules, record: CrmRecordDetail): boolean {
  return hasDestinationPhone(rules, record.contacts.map((contact) => ({
    kind: contact.kind,
    value: contact.value,
    tag_ids: contact.tags.map((tag) => tag.id),
  })));
}

/** "Already created this customer?": picks an existing customer to resolve the exception with instead. */
function ExistingCustomerPicker({ onResolve }: { onResolve: (customerId: string) => void }) {
  const customersResult = useListCustomers();
  const customers: Customer[] = customersResult.data?.success ? customersResult.data.data : [];
  const [customerId, setCustomerId] = useState("");
  const options = customers.map((customer) => ({ value: customer.id, label: `${customer.long_name} (${customer.client_id})` }));

  return (
    <div className="space-y-2 rounded-md border bg-gray-50 p-3" data-testid="customer-exception-existing">
      <Label htmlFor="customer-exception-existing-customer">Already created this customer? Choose it to resolve the exception.</Label>
      <div className="flex gap-2">
        <Combobox
          id="customer-exception-existing-customer"
          isModal
          dwimOnChange
          virtualizeList
          popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
          className="w-full"
          placeholder={customersResult.isLoading ? "Loading customers…" : "Choose a customer"}
          options={options}
          value={customerId}
          onValueChange={setCustomerId}
        />
        <Button variant="outline" disabled={customerId === ""} onClick={() => onResolve(customerId)}>Resolve</Button>
      </div>
    </div>
  );
}

/**
 * Resolves a customer exception: the customer is created (producer_or_carrier) or given a Destination phone
 * (no_phone_number) through CRM first, then nx is told to finish the exception for that customer. If the nx
 * call fails, the customer already exists, so only the nx call is retried; that also works after closing:
 * a producer_or_carrier exception can be resolved with an existing customer, and a no_phone_number one whose
 * customer already has a Destination phone goes straight to resolving. Mount it only while open.
 */
export function CustomerExceptionDialog({ exception, onClose }: { exception: CustomerException; onClose: () => void }) {
  const { toast } = useToast();
  const manageException = useManageException();
  const rulesResult = useGetCrmRules();
  const linkedCustomerId = exception.exception_type === "no_phone_number" ? exception.customer_id : "";
  const linkedResult = useGetCrmMasterDetail({ customer_id: linkedCustomerId }, linkedCustomerId !== "");
  const [resolving, setResolving] = useState<Resolving | null>(null);
  const [isFinishing, setIsFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);

  const finish = async (customerId: string, reason: Resolving["reason"]): Promise<void> => {
    setResolving({ customerId, reason });
    setIsFinishing(true);
    setFinishError(null);
    try {
      const result = await manageException(
        { id: customerId, exception_id: exception.exception_id },
        { exception_type: exception.exception_type },
      );
      if (!result.success) {
        setFinishError(result.message || "The exception couldn't be resolved.");
        return;
      }
      toast({ title: "Exception resolved", description: "" });
      onClose();
    } catch {
      setFinishError("The exception couldn't be resolved.");
    } finally {
      setIsFinishing(false);
    }
  };

  const rules = rulesResult.data;
  const linkedRecord = linkedResult.data?.records.find((record) => record.customer_id === linkedCustomerId);
  const pending: Resolving | null = resolving
    ?? (rules && linkedRecord && storedRecordHasDestinationPhone(rules, linkedRecord)
      ? { customerId: linkedCustomerId, reason: "has_destination_phone" }
      : null);

  if (pending !== null) {
    const attempted = resolving !== null;
    return (
      <Dialog open onOpenChange={(nextOpen: boolean) => { if (!nextOpen && !isFinishing) onClose(); }}>
        <DialogContent className="sm:max-w-md" data-testid="customer-exception-finish">
          <DialogHeader>
            <DialogTitle>Resolve exception</DialogTitle>
            <DialogDescription>
              {pending.reason === "saved" && "The customer was saved in CRM."}
              {pending.reason === "chosen" && "Resolving the exception with the chosen customer."}
              {pending.reason === "has_destination_phone" && "This customer already has a Destination phone number in CRM."}
            </DialogDescription>
          </DialogHeader>
          {isFinishing && (
            <p className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Resolving the exception…
            </p>
          )}
          {!isFinishing && finishError && (
            <p className="text-sm text-red-600">
              {finishError} The customer is saved, so retrying only resolves the exception.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isFinishing}>Close</Button>
            {!isFinishing && (!attempted || finishError) && (
              <Button onClick={() => { void finish(pending.customerId, pending.reason); }} data-testid="customer-exception-retry">
                {attempted && <RefreshCw className="mr-2 h-4 w-4" />}
                {attempted ? "Retry" : "Resolve"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (exception.exception_type === "producer_or_carrier") {
    return (
      <CrmCustomerCreateDialog
        title="Create producer or carrier"
        description={`Creates the CRM record (and its customer) for ${exception.name}, then resolves the exception.`}
        header={<ExistingCustomerPicker onResolve={(customerId: string) => { void finish(customerId, "chosen"); }} />}
        initialValues={(currentRules: CrmRules) => ({
          ...emptyRecordFormValues(""),
          long_name: exception.name,
          short_name: exception.name,
          contacts: destinationPhoneRow(currentRules),
        })}
        recordTypeFilter={allowsDestination}
        validate={requireDestinationPhone}
        onClose={onClose}
        onCreated={(result) => {
          if (result.record.customer_id) {
            void finish(result.record.customer_id, "saved");
            return;
          }
          // Not expected: only customer types are offered, and creating one links its customer.
          toast({
            title: "The record was created without a customer",
            description: "The exception needs a customer: resolve it by choosing the customer above.",
          });
        }}
      />
    );
  }

  return (
    <CrmCustomerEditDialog
      customerId={exception.customer_id}
      prepareValues={(currentRules: CrmRules, values: RecordFormValues) =>
        values.record_type && allowsDestination(currentRules, values.record_type)
          ? { ...values, contacts: [...values.contacts, ...destinationPhoneRow(currentRules)] }
          : values}
      validate={requireDestinationPhone}
      onClose={onClose}
      onSaved={() => { void finish(exception.customer_id, "saved"); }}
    />
  );
}
